import { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiUrl, handleFetchResponse } from '../shared/utils/api';
import { devLog, getEnvAwareValue, wait } from '../../../utils/browser';
import uniqueId from 'lodash/uniqueId';
import { useLoading } from '../../../utils/loading';
import { API_VERSION, CLIENT_VERSION } from '../shared/config';

export function useQueue({
  key = '',
  onComplete,
  onError,
  enqueueEndpoint = '/energia-solar-queue/v2/enqueue',
  pollEndpoint = '/energia-solar-queue/v2/status',
  token = '',
  timeout = 6000,
  retryLimit = 30,
}) {
  const state = useLoading();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadKey = useMemo(() => (!!key ? key : `${uniqueId()}-${Math.random() * 999999}`), []);

  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [lastFetchedData, setLastFetchedData] = useState();

  // const [currentBody, setCurrentBody] = useState();
  const [currentQueueId, setCurrentQueueId] = useState();

  const pollQueueData = useCallback(
    async (id, counter) => {
      try {
        const data = await handleFetchResponse(
          fetch(getApiUrl(`${pollEndpoint}/${encodeURIComponent(id)}`), {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              'x-api-version': API_VERSION,
              'x-client-version': CLIENT_VERSION,
            },
          }),
        );

        if (data?.status === 'failed') {
          const t = new Error(data?.errorMessage);
          devLog('queue error -> ', data);
          t['errorData'] = data?.errorData;
          throw t;
        }

        if (data?.status === 'completed') {
          setPolling(false);
          setLastFetchedData(data.result);

          if (onComplete) {
            onComplete(data.result);
          }

          return data.result;
        }

        if (counter >= retryLimit) {
          throw new Error('Tempo de espera excedido.');
        }

        await wait(timeout);

        return pollQueueData(id, counter + 1);
      } catch (error) {
        setPolling(false);

        if (onError) {
          onError(error);
        }
      }
    },
    [onComplete, onError, pollEndpoint, retryLimit, timeout, token],
  );

  const sendToQueue = useCallback(
    async (body = {}) => {
      if (loading || polling) return;
      setCurrentQueueId(undefined);
      setLoading(true);
      // setCurrentBody(undefined);

      try {
        const data = await handleFetchResponse(
          fetch(getApiUrl(enqueueEndpoint), {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              'x-api-version': API_VERSION,
              'x-client-version': CLIENT_VERSION,
            },
          }),
        );

        if (!data?.queueId) {
          throw new Error(
            getEnvAwareValue(
              'Erro inesperado.',
              `Erro ao enviar para a fila em ${enqueueEndpoint}`,
            ),
          );
        }

        setPolling(true);
        pollQueueData(data.queueId, 0);

        setTimeout(() => {
          setCurrentQueueId(data.queueId);
          // setCurrentBody(body);
        }, 25);
      } catch (error) {
        if (onError) {
          onError(error);
        }
      } finally {
        setLoading(false);
      }
    },
    [enqueueEndpoint, loading, onError, pollQueueData, polling, token],
  );

  const retryQueue = useCallback(async () => {
    if (polling || loading) return;

    if (!currentQueueId) {
      if (onError) {
        onError(new Error('Nenhuma requisição anterior para reenviar.'));
      }
      return;
    }

    setPolling(true);
    pollQueueData(currentQueueId, 0);
  }, [currentQueueId, loading, onError, pollQueueData, polling]);

  useEffect(() => {
    state.setLoading(loadKey, loading || polling);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKey, loading, polling]);

  return {
    retryQueue,
    sendToQueue,
    lastFetchedData,
    loading: loading || polling,
  };
}
